import React, {
  Suspense, lazy, useCallback, useEffect, useMemo, useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { isLinkRefusal, linkProfileApi } from "../Students/profile/profileApi";
import { forgetToken, rememberToken, tokenFrom, withoutToken } from "./linkToken";
import { opensPhoneShell } from "../../utils/phoneShell";

const PhoneProfileLink = lazy(() => import("../StudentMobile/pages/account/PhoneProfileLink"));
const DeskProfileLink = lazy(() => import("../StudentDesk/pages/profile/DeskProfileLink"));

/**
 * PublicProfilePage — `/profile-form?t=<token>`: the emailed link into a
 * student's own profile, without an LMS login. The token leaves the address
 * bar on arrival and lives in sessionStorage for the tab; every request
 * carries it in the body (profileApi.linkProfileApi). A dead link gets one
 * plain page saying so.
 *
 * A phone gets the phone's screens, a laptop or a tablet the student desk's.
 * Neither has a shell around it here, so each draws its own frame.
 */
const PublicProfilePage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    const storage = typeof window !== "undefined" ? window.sessionStorage : null;
    const found = tokenFrom(location.search, storage);
    if (found.token) {
      rememberToken(found.token, storage);
      if (found.fromUrl && typeof window !== "undefined") {
        window.history.replaceState(null, "", withoutToken(window.location.href));
      }
    }
    setToken(found.token || "");
  }, [location.search]);

  const api = useMemo(() => (token ? linkProfileApi(token) : null), [token]);

  const onLinkRefused = useCallback((err) => {
    if (!isLinkRefusal(err)) return;
    forgetToken(typeof window !== "undefined" ? window.sessionStorage : null);
    setRefused(true);
  }, []);

  const dead = refused || token === "";
  const Screen = opensPhoneShell() ? PhoneProfileLink : DeskProfileLink;

  return (
    <Suspense fallback={null}>
      <Screen api={api} dead={dead} focusAgreement={searchParams.get("tab") === "agreement"} onLinkRefused={onLinkRefused} />
    </Suspense>
  );
};

export default PublicProfilePage;
